/**
 * URL search-param model for ``/console/connectors`` — the whole surface is
 * deep-linkable: the active tab (``view``), text query (``q``), status filter
 * (``status``) and the open connect-modal (``connect=<type>``) all live in the
 * URL. ``view=custom`` opens the "I miei connettori" (developer Push-API) tab;
 * absent/``catalog`` shows the white-label vendor catalog.
 */

import type { ConsoleConnectorStatus } from "../api/types";

const STATUS_FILTERS: readonly ConsoleConnectorStatus[] = ["connected", "not_connected", "error"];

/** Which tab of the Connettori surface is shown. */
export const CONSOLE_CONNECTORS_VIEWS = ["catalog", "custom"] as const;
export type ConsoleConnectorsView = (typeof CONSOLE_CONNECTORS_VIEWS)[number];

/** Tabs of the per-connector detail (``CustomConnectorDetail``). */
export const CUSTOM_CONNECTOR_DETAIL_TABS = [
  "actions",
  "syncs",
  "validate",
  "contract",
  "setup",
] as const;
export type CustomConnectorDetailTab = (typeof CUSTOM_CONNECTOR_DETAIL_TABS)[number];

export interface ConsoleConnectorsSearch {
  /** Active tab; absent = ``catalog``. */
  view?: ConsoleConnectorsView;
  /** Free-text filter on connector label/type. */
  q?: string;
  /** Status facet; absent = all. */
  status?: ConsoleConnectorStatus;
  /** Connector TYPE whose connect-modal is open. */
  connect?: string;
  /** Custom-connector SLUG whose detail panel is open (implies the custom tab). */
  custom?: string;
  /** Active tab of the open custom-connector detail; absent = ``actions``. */
  ctab?: CustomConnectorDetailTab;
  /**
   * Custom-list facet: ``hide`` hides the disabled connectors. Absent = show
   * them (the default — data is never hidden unless the dev asks).
   */
  cdis?: "hide";
}

function isConnectorStatus(value: unknown): value is ConsoleConnectorStatus {
  return typeof value === "string" && (STATUS_FILTERS as readonly string[]).includes(value);
}

function isConnectorsView(value: unknown): value is ConsoleConnectorsView {
  return (
    typeof value === "string" && (CONSOLE_CONNECTORS_VIEWS as readonly string[]).includes(value)
  );
}

function isCustomDetailTab(value: unknown): value is CustomConnectorDetailTab {
  return (
    typeof value === "string" && (CUSTOM_CONNECTOR_DETAIL_TABS as readonly string[]).includes(value)
  );
}

/** Drop anything malformed instead of erroring — bad deep links degrade to "all". */
export function validateConsoleConnectorsSearch(
  search: Record<string, unknown>,
): ConsoleConnectorsSearch {
  const out: ConsoleConnectorsSearch = {};
  // ``catalog`` is the default, so it never needs to sit in the URL.
  if (isConnectorsView(search.view) && search.view !== "catalog") {
    out.view = search.view;
  }
  if (typeof search.q === "string" && search.q.length > 0) {
    out.q = search.q.slice(0, 120);
  }
  if (isConnectorStatus(search.status)) {
    out.status = search.status;
  }
  if (typeof search.connect === "string" && search.connect.length > 0) {
    out.connect = search.connect.slice(0, 64);
  }
  if (typeof search.custom === "string" && search.custom.length > 0) {
    out.custom = search.custom.slice(0, 64);
    // ``ctab`` only makes sense with an open detail; ``actions`` is the
    // default and never needs to sit in the URL.
    if (isCustomDetailTab(search.ctab) && search.ctab !== "actions") {
      out.ctab = search.ctab;
    }
  }
  // Showing disabled connectors is the default — only ``hide`` is stored.
  if (search.cdis === "hide") {
    out.cdis = "hide";
  }
  return out;
}
